import { Injectable } from '@angular/core';
import { ProductListApiService } from '@epicuro-next/data/engagement/engagement-api';
import { ProductService } from '@epicuro-next/data/product';
import { List, MediaArgs } from '@epicuro-next/models/engagement';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import {
  hideLoaderOverlay,
  showLoaderOverlay,
} from '@epicuro-next/state/user-ui-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { Product } from 'graphql-api/gql-api-interfaces';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  shareReplay,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import * as actions from '../../actions';
import {
  selectAllowEpicShow,
  selectIsOwner,
  selectOpenedList,
} from '../../selectors';

export const MY_LIST_PREFIX = $localize`:ProductList|My epic show text@@myEpicShowText:My`;
export const EPIC_SHOW_CREATE_MESSAGE = $localize`:ProductList|Epic Show create success message@@epicShowCreateSuccessMessage:Your Epic Show was created`;

@Injectable()
export class EpicShowProductsEffects {
  private patchedProducts$: BehaviorSubject<Product[] | undefined> =
    new BehaviorSubject(undefined);

  private siteId$ = this.store.pipe(select(selectSiteId), filter(isNonNullish));

  private accountId$ = this.store.pipe(
    select(selectAccountId),
    filter<string>(Boolean),
  );

  private openedList$ = this.store.pipe(select(selectOpenedList));

  private session$ = combineLatest([this.siteId$, this.accountId$]).pipe(
    map(([siteId, accountId]) => ({ siteId, accountId })),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public loadEpicShowProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadEpicShowProducts),
      filter(({ ids }) => !!ids.length),
      withLatestFrom(this.session$),
      switchMap(([{ ids }, { siteId }]) => {
        return this.productService
          .productsByReferences({
            site: siteId ?? '',
            num: ids?.length,
            productReferences: ids.map((productId) => ({
              brandId: siteId?.toLowerCase() ?? '',
              productId,
            })),
          })
          .pipe(
            map(({ products }) =>
              actions.loadEpicShowProductsSuccess({
                products,
              }),
            ),
            catchError(() => of(actions.loadOpenedListProductsError())),
          );
      }),
    ),
  );

  public scanPatchedProduct$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.patchEpicShowProductInList),
        withLatestFrom(
          this.openedList$,
          this.store.pipe(select(selectIsOwner())),
          this.store.pipe(select(selectAllowEpicShow)),
        ),
        filter(([, list, isOwner, allowEpicShow]) => {
          return Boolean(list && !isOwner && !allowEpicShow);
        }),
        tap(([{ product }]) => {
          const current = this.patchedProducts$.getValue() ?? [];
          this.patchedProducts$.next([...current, product]);
        }),
      ),
    { dispatch: false },
  );

  public patchEpicShowProductInList$ = createEffect(() =>
    this.patchedProducts$.pipe(
      filter((products) => {
        return Boolean(products && products.length);
      }),
      concatLatestFrom(() => [this.openedList$, this.accountId$, this.siteId$]),
      filter(([, list]) => {
        return Boolean(list);
      }),
      exhaustMap(([, list, accountId, siteId]) => {
        this.store.dispatch(showLoaderOverlay());
        const products = this.patchedProducts$.getValue() ?? [];
        const productsReference = products.map(
          ({ productId, brandId, isAvailable }) => ({
            productId,
            brandId,
            isAvailable,
          }),
        );
        return this.productListApi
          .createListWithProducts({
            name: `${MY_LIST_PREFIX} ${(list as List).name}`,
            description: list?.description,
            media: list?.mainMedia ? [list?.mainMedia as MediaArgs] : undefined,
            originalProductListId: list?.id,
            productReferences: productsReference,
            accountId,
            siteId,
          })
          .pipe(
            switchMap((item: List) => {
              this.patchedProducts$.next(undefined);

              return [
                actions.createListSuccess({ list: item }),
                hideLoaderOverlay({ delay: 2000 }),
              ];
            }),
            catchError((err) => [actions.createListError({ error: err })]),
          );
      }),
    ),
  );

  public patchActiveProductList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.patchEpicShowProductInList),
      withLatestFrom(
        this.store.pipe(select(selectAllowEpicShow)),
        this.store.pipe(select(selectIsOwner())),
      ),
      filter(([, allowEpicShow, isOwner]) => allowEpicShow || isOwner),
      map(([{ product }]) => actions.patchActiveProductList({ product })),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private productService: ProductService,
    private productListApi: ProductListApiService,
  ) {}
}
